import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    updateDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';

/**
 * Message interface matching Chat.tsx
 */
export interface Message {
    id: string;
    content: string;
    userId: string;
    createdAt: Date;
    isEditing?: boolean;
    role: 'user' | 'assistant';
    module?: {
        type: string;
        id?: string;
        title?: string;
        description?: string;
        userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en';
        isDynamic?: boolean;
        roadmapData?: {
            title: string;
            description: string;
            [key: string]: any;
        };
    };
    planContext?: {
        planId: string;
        day: number;
        contextType: 'goal' | 'activity' | 'tip' | 'parent_sharing' | 'general';
        contextElement?: string;
        conversationFlow: 'question' | 'answer' | 'clarification' | 'follow_up';
    };
}

/**
 * Custom hook for managing chat messages
 * 
 * Handles:
 * - Firestore message synchronization
 * - Message CRUD operations
 * - Typing indicator state
 * - Conversation title updates
 * 
 * @param conversationId - Current conversation ID
 * @param userId - Current user ID
 * @param onError - Error callback function
 */
export const useChatMessages = (
    conversationId: string,
    userId: string | undefined,
    onError?: (error: string) => void
) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isTyping, setIsTyping] = useState(false);

    /**
     * Set up Firestore listener for messages
     */
    useEffect(() => {
        if (!userId || !conversationId) {
            console.log('useChatMessages: No user or conversationId:', {
                hasUser: !!userId,
                conversationId
            });
            setIsLoading(false);
            return;
        }

        console.log('useChatMessages: Setting up Firestore listener for conversation:', conversationId);
        setIsLoading(true);

        const q = query(
            collection(db, `conversations/${conversationId}/messages`),
            orderBy('createdAt', 'asc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                console.log('useChatMessages: Firestore update received:', {
                    messageCount: snapshot.docs.length,
                    messages: snapshot.docs.map(doc => ({
                        id: doc.id,
                        content: doc.data().content,
                        role: doc.data().role,
                        userId: doc.data().userId
                    }))
                });

                const messageList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate(),
                })) as Message[];

                setMessages(messageList);
                setIsLoading(false);
            },
            (error) => {
                console.error('useChatMessages: Firestore listener error:', error);
                onError?.(error.message);
                setIsLoading(false);
            }
        );

        return () => {
            console.log('useChatMessages: Cleaning up Firestore listener');
            unsubscribe();
        };
    }, [conversationId, userId]); // Removed onError to prevent infinite loop


    /**
     * Add a new message to the conversation
     */
    const addMessage = useCallback(async (
        content: string,
        role: 'user' | 'assistant',
        additionalData?: Partial<Message>
    ): Promise<string> => {
        if (!userId || !conversationId) {
            throw new Error('User ID or Conversation ID is missing');
        }

        try {
            const messageData = {
                content,
                role,
                userId,
                createdAt: serverTimestamp(),
                ...additionalData
            };

            const docRef = await addDoc(
                collection(db, `conversations/${conversationId}/messages`),
                messageData
            );

            console.log('useChatMessages: Message added:', docRef.id);
            return docRef.id;
        } catch (error) {
            console.error('useChatMessages: Error adding message:', error);
            throw error;
        }
    }, [conversationId, userId]);

    /**
     * Update an existing message
     */
    const updateMessage = useCallback(async (
        messageId: string,
        updates: Partial<Message>
    ): Promise<void> => {
        if (!conversationId) {
            throw new Error('Conversation ID is missing');
        }

        try {
            const messageRef = doc(db, `conversations/${conversationId}/messages/${messageId}`);
            await updateDoc(messageRef, updates as any);
            console.log('useChatMessages: Message updated:', messageId);
        } catch (error) {
            console.error('useChatMessages: Error updating message:', error);
            throw error;
        }
    }, [conversationId]);

    /**
     * Update conversation title (typically from first message)
     */
    const updateConversationTitle = useCallback(async (firstMessage: string): Promise<void> => {
        if (!conversationId) {
            throw new Error('Conversation ID is missing');
        }

        try {
            const title = firstMessage.trim().substring(0, 50);
            const finalTitle = title.length < firstMessage.trim().length ? title + '...' : title;

            await updateDoc(doc(db, `conversations/${conversationId}`), {
                title: finalTitle,
                lastMessage: firstMessage.substring(0, 100),
                updatedAt: new Date(),
            });

            console.log('useChatMessages: Conversation title updated:', finalTitle);
        } catch (error) {
            console.error('useChatMessages: Error updating conversation title:', error);
            // Don't throw - title update is not critical
        }
    }, [conversationId]);

    return {
        messages,
        isLoading,
        isTyping,
        setIsTyping,
        addMessage,
        updateMessage,
        updateConversationTitle
    };
};
